import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import BrandWordmark from '../components/BrandWordmark';
import './SeoGuidePage.css';

const GUIDE_CONTENT = {
  'instagram-followers': {
    title: 'Buy Instagram Followers in Africa',
    description: 'Learn how Instagram follower services work, what to expect, and how to choose safer growth options for creators and brands in Africa.',
    updated: 'April 26, 2026',
    intro: 'Instagram follower services can help make a profile look more established, but the quality of followers matters more than the raw number. The best approach is to combine follower growth with reels, story views, and real engagement.',
    sections: [
      {
        heading: 'What Instagram follower services do',
        body: [
          'These services usually deliver followers to a public Instagram profile after you submit the correct username or profile link.',
          'Some follower services are refill-based, some are non-refill, and some are country-targeted. Always check the service description before ordering.',
        ],
      },
      {
        heading: 'How to choose a better service',
        body: [
          'Look for realistic delivery speed, clear refill policy, and a provider that also offers supporting services like views, likes, and story engagement.',
          'Avoid placing too many orders on the same profile at once. Gradual growth usually looks healthier than a sudden spike.',
        ],
      },
      {
        heading: 'Best practice for creators and businesses',
        body: [
          'Use follower services as social proof support, not as the only strategy. Pair them with reels, collaborations, and strong profile branding.',
          'For brands in Africa, localized offers, WhatsApp conversion, and strong landing pages often matter more than follower count alone.',
        ],
      },
    ],
  },
  'tiktok-views': {
    title: 'Buy TikTok Views and Engagement',
    description: 'Understand TikTok view services, how they differ from likes and followers, and how to use them more safely for campaigns.',
    updated: 'April 26, 2026',
    intro: 'TikTok views are one of the most common starter services because they can help content look active quickly. Views are usually best used alongside likes, shares, and follower growth.',
    sections: [
      {
        heading: 'What TikTok views can help with',
        body: [
          'Views can improve the first impression on a video and make new visitors more likely to watch content that already appears active.',
          'For campaigns, views are often combined with likes or saves so the post does not look unbalanced.',
        ],
      },
      {
        heading: 'Views vs likes vs followers',
        body: [
          'Views increase visibility on a single post. Likes show lightweight engagement. Followers affect overall profile social proof.',
          'A healthier campaign usually mixes these instead of pushing only one metric.',
        ],
      },
      {
        heading: 'How to order more safely',
        body: [
          'Use the exact video link, keep the post public, and avoid stacking many overlapping orders too quickly.',
          'If you are promoting a product or music clip, match the order size to the actual reach you expect so the result looks natural.',
        ],
      },
    ],
  },
  'telegram-members': {
    title: 'Buy Telegram Members for Channels and Groups',
    description: 'A practical guide to Telegram member services, channel growth, and how to combine members with views and reactions.',
    updated: 'April 26, 2026',
    intro: 'Telegram member services are commonly used to help new channels and groups look more established. They are often most effective when paired with post views and reactions.',
    sections: [
      {
        heading: 'What Telegram member services do',
        body: [
          'These services add members to a Telegram channel or group, depending on the service type.',
          'Some are refill-based, while others are cheaper but may have more fluctuation over time.',
        ],
      },
      {
        heading: 'Why views and reactions matter too',
        body: [
          'A channel with many members but very low post views can look weak. Matching member growth with post views usually gives a better result.',
          'Reactions can also help channels appear more active to new visitors.',
        ],
      },
      {
        heading: 'Tips for channel admins',
        body: [
          'Keep the invite link correct and public, post regularly, and make sure the channel has a clear topic before ordering growth.',
          'If your goal is sales, include strong call-to-action posts and simple joining instructions.',
        ],
      },
    ],
  },
  'website-traffic-explained': {
    title: 'Website Traffic Services Explained',
    description: 'Learn the difference between website traffic, SEO traffic, and social traffic before buying traffic services.',
    updated: 'April 26, 2026',
    intro: 'Website traffic services are usually designed to send visits to a URL. They can be useful for some visibility experiments, but they are not the same as real conversions or long-term SEO growth.',
    sections: [
      {
        heading: 'Types of traffic services',
        body: [
          'Generic website traffic focuses on visits. SEO traffic is meant to look more like search traffic. Social traffic is meant to look like visits from platforms such as Facebook or Twitter/X.',
          'Different services may also offer country targeting, device targeting, or referrer selection.',
        ],
      },
      {
        heading: 'When traffic services are useful',
        body: [
          'They can help with testing landing pages, collecting rough analytics signals, or supporting awareness campaigns where raw visits matter.',
          'They are not a reliable replacement for real organic traffic, search optimization, or paid ad conversion campaigns.',
        ],
      },
      {
        heading: 'What to watch out for',
        body: [
          'Do not rely on bought traffic to earn money from ad networks. That usually creates more risk than upside.',
          'If your goal is business growth, combine useful content, search intent pages, and real conversion funnels instead of depending only on traffic packages.',
        ],
      },
    ],
  },
};

const GUIDE_LINKS = [
  { slug: 'instagram-followers', label: 'Instagram Followers' },
  { slug: 'tiktok-views', label: 'TikTok Views' },
  { slug: 'telegram-members', label: 'Telegram Members' },
  { slug: 'website-traffic-explained', label: 'Website Traffic' },
];

export default function SeoGuidePage({ slug }) {
  const guide = GUIDE_CONTENT[slug] || GUIDE_CONTENT['instagram-followers'];

  useEffect(() => {
    const previousTitle = document.title;
    const previousDescription = document.querySelector('meta[name="description"]')?.getAttribute('content') || '';

    document.title = `${guide.title} | NiceBooster`;

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', guide.description);

    return () => {
      document.title = previousTitle;
      if (metaDescription) {
        metaDescription.setAttribute('content', previousDescription);
      }
    };
  }, [guide.description, guide.title]);

  return (
    <div className="seo-guide-page">
      <div className="seo-guide-shell">
        <header className="seo-guide-top">
          <Link to="/" className="seo-guide-brand-link">
            <BrandWordmark className="seo-guide-brand" />
          </Link>
          <div className="seo-guide-nav">
            <Link to="/login" className="seo-guide-nav-link">Login</Link>
            <Link to="/register" className="btn btn-primary">Sign Up</Link>
          </div>
        </header>

        <main className="seo-guide-card">
          <div className="seo-guide-hero">
            <p className="seo-guide-kicker">NiceBooster Guide</p>
            <h1>{guide.title}</h1>
            <p className="seo-guide-description">{guide.description}</p>
            <p className="seo-guide-updated">Updated: {guide.updated}</p>
          </div>

          <div className="seo-guide-content">
            <p>{guide.intro}</p>

            {guide.sections.map((section) => (
              <section key={section.heading} className="seo-guide-section">
                <h2>{section.heading}</h2>
                {section.body.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </section>
            ))}
          </div>

          <aside className="seo-guide-links">
            <h3>More guides</h3>
            <div className="seo-guide-links-list">
              {GUIDE_LINKS.filter((link) => link.slug !== slug).map((link) => (
                <Link key={link.slug} to={`/guides/${link.slug}`} className="seo-guide-link-card">
                  {link.label}
                </Link>
              ))}
            </div>
          </aside>

          <div className="seo-guide-actions">
            <Link to="/register" className="btn btn-primary btn-lg">Create Free Account</Link>
            <Link to="/login" className="btn btn-secondary btn-lg">Open Dashboard</Link>
          </div>
        </main>
      </div>
    </div>
  );
}
